import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatDateTime, useOvertimeBookings } from "../hooks/useWalletAdminData";
import { markOvertimeReviewed } from "../services/walletAdminService";
import { canMutateWallet, canViewWalletModule } from "../utils/walletPermissions";
import "./walletAdmin.css";

export default function OvertimeMonitoring() {
  const navigate = useNavigate();
  const allowed = canViewWalletModule();
  const canMutate = canMutateWallet();

  const [artist, setArtist] = useState("");
  const [reason, setReason] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [working, setWorking] = useState(false);

  const { rows, loading, error } = useOvertimeBookings({
    artist,
    reason,
    dateFrom,
    dateTo,
  });

  const summary = useMemo(() => rows.reduce((acc, row) => {
    acc.count += Number(row.overtimeRequestCount || 0);
    acc.minutes += Number(row.overtimeExtraMinutes || 0);
    if (row.overtimeReviewed) acc.reviewed += 1;
    return acc;
  }, { count: 0, minutes: 0, reviewed: 0 }), [rows]);

  if (!allowed) {
    return (
      <div className="wallet-card wallet-page">
        <h3 style={{ marginTop: 0 }}>Access Denied</h3>
        <p>You do not have permission to open overtime monitoring.</p>
      </div>
    );
  }

  const onReviewUpdate = async (row, reviewed) => {
    if (!canMutate) {
      toast.error("No permission to update overtime review");
      return;
    }
    setWorking(true);
    try {
      await markOvertimeReviewed({
        bookingId: row.id,
        reviewed,
        note: noteDraft || row.overtimeAdminNote || "",
      });
      toast.success(reviewed ? "Marked reviewed" : "Marked unreviewed");
      setSelected(null);
      setNoteDraft("");
    } catch (e) {
      toast.error(e?.message || "Failed to update review");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="wallet-page">
      <div className="wallet-title-row">
        <div>
          <button className="wallet-btn wallet-btn-light" onClick={() => navigate("/panel/wallets")}>
            <FaArrowLeft style={{ marginRight: 6 }} />
            Back to Wallets
          </button>
          <h2 style={{ margin: "10px 0 0" }}>Overtime Monitoring</h2>
        </div>
      </div>

      <div className="wallet-grid" style={{ marginBottom: 12 }}>
        <div className="wallet-card">
          <div style={{ color: "#64748b", fontSize: 12 }}>Overtime Requests</div>
          <div style={{ fontSize: 24, fontWeight: 800 }}>{summary.count}</div>
        </div>
        <div className="wallet-card">
          <div style={{ color: "#64748b", fontSize: 12 }}>Extra Minutes</div>
          <div style={{ fontSize: 24, fontWeight: 800 }}>{summary.minutes}</div>
        </div>
        <div className="wallet-card">
          <div style={{ color: "#64748b", fontSize: 12 }}>Reviewed Bookings</div>
          <div style={{ fontSize: 24, fontWeight: 800 }}>{summary.reviewed}</div>
        </div>
      </div>

      <div className="wallet-card" style={{ marginBottom: 12 }}>
        <div className="wallet-grid-2">
          <input
            className="wallet-input"
            placeholder="Filter by artist name/phone"
            value={artist}
            onChange={(e) => setArtist(e.target.value)}
          />
          <input
            className="wallet-input"
            placeholder="Filter by reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <input
            className="wallet-input"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
          <input
            className="wallet-input"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
      </div>

      <div className="wallet-card">
        {loading ? <div className="wallet-empty">Loading overtime bookings...</div> : null}
        {!loading && error ? <div className="wallet-empty">{error}</div> : null}
        {!loading && !error && !rows.length ? <div className="wallet-empty">No overtime data found.</div> : null}

        {!loading && !error && rows.length ? (
          <div className="wallet-table-wrap">
            <table className="wallet-table">
              <thead>
                <tr>
                  <th>Booking ID</th>
                  <th>Artist</th>
                  <th>Requests</th>
                  <th>Extra Minutes</th>
                  <th>Reason</th>
                  <th>Last Note</th>
                  <th>Last Requested At</th>
                  <th>Review</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{row.artistName || row.artist_name || row.artistPhone || row.artist_uid || "-"}</td>
                    <td>{row.overtimeRequestCount || 0}</td>
                    <td>{row.overtimeExtraMinutes || 0}</td>
                    <td>{row.overtimeLastReason || "-"}</td>
                    <td>{row.overtimeLastNote || "-"}</td>
                    <td>{formatDateTime(row.overtimeLastRequestedAt)}</td>
                    <td>
                      <button
                        className="wallet-btn wallet-btn-light"
                        onClick={() => {
                          setSelected(row);
                          setNoteDraft(row.overtimeAdminNote || "");
                        }}
                      >
                        {row.overtimeReviewed ? "Reviewed" : "Pending"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {selected ? <div className="wallet-drawer-backdrop" onClick={() => setSelected(null)} /> : null}
      {selected ? (
        <aside className="wallet-drawer">
          <div className="wallet-title-row">
            <h3 style={{ margin: 0 }}>Overtime Review</h3>
            <button className="wallet-btn wallet-btn-light" onClick={() => setSelected(null)}>
              Close
            </button>
          </div>

          <div className="wallet-card" style={{ marginBottom: 12 }}>
            <dl className="wallet-kv">
              <dt>Booking ID</dt><dd>{selected.id}</dd>
              <dt>Artist</dt><dd>{selected.artistName || selected.artist_name || selected.artistPhone || selected.artist_uid || "-"}</dd>
              <dt>Requests</dt><dd>{selected.overtimeRequestCount || 0}</dd>
              <dt>Extra Minutes</dt><dd>{selected.overtimeExtraMinutes || 0}</dd>
              <dt>Reason</dt><dd>{selected.overtimeLastReason || "-"}</dd>
              <dt>Last Note</dt><dd>{selected.overtimeLastNote || "-"}</dd>
              <dt>Requested At</dt><dd>{formatDateTime(selected.overtimeLastRequestedAt)}</dd>
              <dt>Reviewed</dt><dd>{selected.overtimeReviewed ? "Yes" : "No"}</dd>
            </dl>
          </div>

          <div className="wallet-card">
            <h4 style={{ marginTop: 0 }}>Admin Note</h4>
            <textarea
              className="wallet-textarea"
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="Add review note"
            />

            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button
                className="wallet-btn wallet-btn-primary"
                disabled={working || !canMutate}
                onClick={() => onReviewUpdate(selected, true)}
              >
                Mark Reviewed
              </button>
              <button
                className="wallet-btn wallet-btn-secondary"
                disabled={working || !canMutate}
                onClick={() => onReviewUpdate(selected, false)}
              >
                Mark Unreviewed
              </button>
            </div>
          </div>
        </aside>
      ) : null}

      <ToastContainer position="top-right" autoClose={2600} />
    </div>
  );
}
